import { Block } from '../block.js'
import { ForBlock } from '../logic/for-block.js'
import { TextBlock } from './text-block.js'
import type { TsCodeType, EventType, NameAndType } from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { Expression } from '../../data/text-or-code.js'
import { DraggableBlock } from './draggable-block.js'
import { Parameter } from '../../script/parameter.js'

export const DropListBlockDirections = {
	horizontal: 'horizontal',
	vertical: 'vertical',
	mixed: 'horizontal grid (mixed)',
}
export type DropListBlockDirection = keyof typeof DropListBlockDirections

@jsonClass('DropListBlock')
export class DropListBlock extends Block {
	@jsonProperty(Expression)
	@ScreenEditorInput<DraggableBlock>({
		inputType: 'code',
		order: 1,
		label: 'Data list',
		codeLanguage: 'ts',
		tsReturnType: () => 'any[]',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
	})
	dataListExpression: Expression = new Expression('')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 2,
		label: 'Direction',
		options: Object.entries(DropListBlockDirections),
	})
	direction: DropListBlockDirection = 'vertical'

	constructor(init: Partial<DropListBlock> = {}) {
		super()
		this.init(init)
	}

	static createNewForEditor(): Block {
		const forBlock = new ForBlock()
		forBlock.children = [
			new DraggableBlock({
				dataExpression: new Expression(forBlock.loopVariableDefinition),
			}),
		]
		return new this({
			children: [forBlock],
		})
	}

	get allowedEventTypes(): EventType[] {
		return [
			{
				name: 'cdkDropListDropped',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragDrop<any>',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'cdkDropListEntered',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragEnter<any>',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'cdkDropListExited',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragExit<any>',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'cdkDropListSorted',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragSortEvent<any>',
						defaultExpression: '$event',
					}),
				],
			},
		]
	}

	getPreviewDirection(): 'row' | 'column' {
		return this.parent?.getPreviewDirection() ?? 'row'
	}

	getEditorCategory(): string {
		return 'Others'
	}
}
